import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, Clock, Menu } from 'lucide-react';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import CountryCodeSelect from '../components/CountryCodeSelect';

// Remove a repeated country code the user may have typed (e.g. "+44", "0044",
// or a bare "44") so they only enter the national number. Leading national
// trunk prefixes (e.g. a UK leading 0) are left for libphonenumber to handle.
const stripDuplicateCode = (input, dial) => {
    let s = (input || '').replace(/[^\d+]/g, '');
    const code = dial.replace('+', '');
    if (s.startsWith('+' + code)) return s.slice(code.length + 1);
    if (s.startsWith('00' + code)) return s.slice(code.length + 2);
    if (s.startsWith('+')) return s.slice(1); // a different/leading + — drop the plus
    return s;
};

// True only when the entered number is a valid number for the chosen country.
const isPhoneValid = (national, iso2) => {
    if (!national) return false;
    const parsed = parsePhoneNumberFromString(national, iso2.toUpperCase());
    return !!parsed && parsed.isValid();
};

const WHATSAPP_URL = `https://wa.me/447757233260?text=${encodeURIComponent(
    `Hi! I just completed the qualification form on your website. Looking forward to discussing how Qorvix can help my business.`
)}`;

export default function QualificationFunnelPage() {
    const [currentStep, setCurrentStep] = useState(0);
    const [showForm, setShowForm] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [noWebsite, setNoWebsite] = useState(false);
    const [leadId, setLeadId] = useState(null);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        countryIso2: 'gb',
        countryCode: '+44',
        phone: '',
        companyName: '',
        website: '',
        companyAddress: '',
        companyDescription: '',
        businessType: '',
        growthStage: '',
        bottleneck: '',
        currentSystem: '',
        businessImpact: '',
        desiredOutcome: '',
        budget: '',
        timeline: '',
        decisionRole: '',
        motivation: ''
    });

    // FIX: Scroll to top when form opens
    useEffect(() => {
        if (showForm) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showForm]);

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    // Phone: strip any repeated country code, then live-format for the country.
    const handlePhoneChange = (raw) => {
        const national = stripDuplicateCode(raw, formData.countryCode);
        const formatted = new AsYouType(formData.countryIso2.toUpperCase()).input(national);
        setFormData((prev) => ({ ...prev, phone: formatted }));
    };

    // Country change: update code + ISO, then re-format the existing number.
    const handleCountryChange = (country) => {
        setFormData((prev) => {
            const national = stripDuplicateCode(prev.phone, country.dial);
            const formatted = new AsYouType(country.iso2.toUpperCase()).input(national);
            return { ...prev, countryIso2: country.iso2, countryCode: country.dial, phone: formatted };
        });
    };

    const phoneValid = isPhoneValid(formData.phone, formData.countryIso2);
    const phoneError = formData.phone.length > 0 && !phoneValid;

    // Save the lead's contact details as soon as step 1 is done, so the backend
    // can send a recovery email if they abandon the funnel. Fire-and-forget:
    // never blocks the user from advancing. Skips if already saved.
    const savePartialLead = () => {
        if (leadId) return;
        const e164 =
            parsePhoneNumberFromString(formData.phone, formData.countryIso2.toUpperCase())?.number ||
            `${formData.countryCode} ${formData.phone}`.trim();
        fetch(`${process.env.REACT_APP_API_URL}/api/partial-lead`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fullName: formData.fullName, email: formData.email, phone: e164 }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => { if (data?.leadId) setLeadId(data.leadId); })
            .catch((err) => console.error('Partial lead save failed:', err));
    };

    const handleNext = () => {
        if (currentStep < 11) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/qualification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    leadId,
                    phone:
                        parsePhoneNumberFromString(
                            formData.phone,
                            formData.countryIso2.toUpperCase()
                        )?.number || `${formData.countryCode} ${formData.phone}`.trim(),
                    timestamp: new Date().toISOString()
                })
            });

            if (response.ok) {
                setSubmitSuccess(true);

                // Force redirect to WhatsApp — navigation (not popup) is never blocked
                setTimeout(() => {
                    window.location.href = WHATSAPP_URL;
                }, 1500);
            } else {
                alert('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            alert('Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif]">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-white shadow-md">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo-qorvix.png" alt="Qorvix Logo" className="h-10 w-auto" />
                        </Link>

                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <Link to="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</Link>
                                        <Link to="/integrations" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</Link>
                                        <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                                        <Link to="/help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</Link>
                                        <Link to="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</Link>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button className="bg-orange-500 text-white px-6 py-2.5 rounded-lg hover:bg-orange-600 transition-all font-medium text-[15px]">
                                Start Free Demo
                            </button>
                        </div>

                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-gray-900">
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Modal */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        <button onClick={() => setMobileMenuOpen(false)} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                            <X size={24} />
                        </button>
                        <nav className="mt-4 space-y-6">
                            <Link to="/" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                            <Link to="/about" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                            <Link to="/how-it-works" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>How it works</Link>
                            <Link to="/portfolio" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>portfolio</Link>
                            <Link to="/contact" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact us</Link>
                            <button className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">Start free demo</button>
                        </nav>
                    </div>
                </div>
            )}

            {!showForm ? (
                <div className="max-w-5xl mx-auto px-4 pt-40 pb-20">
                    {/* Outcome Headline */}
                    <section className="text-center mb-20">
                        <h1 className="text-5xl md:text-6xl font-bold mb-6">
                            Turn <span className="text-gradient">Every Lead</span> Into Revenue<br />
                            Without Lifting A Finger
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            Automate your lead response, qualification, and follow-up so you never miss a sale again — even while you sleep.
                        </p>
                    </section>

                    {/* Mechanism */}
                    <section className="mb-20 bg-gradient-to-br from-orange-50 to-white rounded-3xl p-12">
                        <h2 className="text-3xl font-bold mb-8 text-center">How It Works</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">1</div>
                                <h3 className="font-bold text-xl mb-2">AI Captures Every Lead</h3>
                                <p className="text-gray-600">WhatsApp, email, forms — instantly acknowledged in under 30 seconds.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">2</div>
                                <h3 className="font-bold text-xl mb-2">Qualifies & Books Automatically</h3>
                                <p className="text-gray-600">AI asks the right questions, filters serious buyers, schedules calls.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">3</div>
                                <h3 className="font-bold text-xl mb-2">Hands You Ready-To-Close Leads</h3>
                                <p className="text-gray-600">Your team only talks to qualified, high-intent prospects.</p>
                            </div>
                        </div>
                    </section>

                    {/* Who It's For / Not For */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold mb-8 text-center">Is This For You?</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-8">
                                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                                    <Check className="text-green-600" size={32} />
                                    Perfect If You...
                                </h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3">
                                        <Check className="text-green-600 flex-shrink-0 mt-1" size={20} />
                                        <span>Get 20+ leads per week but struggle to respond fast enough</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check className="text-green-600 flex-shrink-0 mt-1" size={20} />
                                        <span>Lose deals because follow-up falls through the cracks</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check className="text-green-600 flex-shrink-0 mt-1" size={20} />
                                        <span>Want to scale without hiring more sales staff</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check className="text-green-600 flex-shrink-0 mt-1" size={20} />
                                        <span>Run a service business, agency, or local operation</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check className="text-green-600 flex-shrink-0 mt-1" size={20} />
                                        <span>Are ready to invest in systems that actually work</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-red-50 border-2 border-red-500 rounded-2xl p-8">
                                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                                    <X className="text-red-600" size={32} />
                                    Not For You If...
                                </h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3">
                                        <X className="text-red-600 flex-shrink-0 mt-1" size={20} />
                                        <span>You get fewer than 10 leads per month</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <X className="text-red-600 flex-shrink-0 mt-1" size={20} />
                                        <span>You prefer doing everything manually "just in case"</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <X className="text-red-600 flex-shrink-0 mt-1" size={20} />
                                        <span>You're looking for a magic button with zero setup</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <X className="text-red-600 flex-shrink-0 mt-1" size={20} />
                                        <span>You don't have a clear sales process yet</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <X className="text-red-600 flex-shrink-0 mt-1" size={20} />
                                        <span>You're not willing to invest in growth systems</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Problem Reframe */}
                    <section className="mb-20 bg-slate-900 text-white rounded-3xl p-12">
                        <h2 className="text-3xl font-bold mb-6 text-center">The Real Problem Isn't Lead Volume</h2>
                        <p className="text-xl text-gray-300 text-center max-w-3xl mx-auto mb-8">
                            You're getting leads. But by the time you respond, they've already contacted 3 other competitors. The one who responds first wins.
                        </p>
                        <div className="grid md:grid-cols-3 gap-6 text-center">
                            <div>
                                <div className="text-5xl font-bold text-orange-500 mb-2">78%</div>
                                <p className="text-gray-400">of buyers choose the business that responds first</p>
                            </div>
                            <div>
                                <div className="text-5xl font-bold text-orange-500 mb-2">5 min</div>
                                <p className="text-gray-400">Response time = 10x higher conversion rate</p>
                            </div>
                            <div>
                                <div className="text-5xl font-bold text-orange-500 mb-2">60%</div>
                                <p className="text-gray-400">of leads are lost to slow follow-up</p>
                            </div>
                        </div>
                    </section>

                    {/* Proof */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold mb-12 text-center">Real Results From Real Businesses</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="bg-gray-50 rounded-2xl p-8">
                                <div className="text-orange-500 text-4xl font-bold mb-4">+47%</div>
                                <h4 className="font-bold text-lg mb-2">Conversion Rate Increase</h4>
                                <p className="text-gray-600 text-sm mb-4">"We respond to every lead in 30 seconds now. Our close rate jumped from 12% to 18% in one month."</p>
                                <p className="text-sm font-semibold">— Luxury Motors UK</p>
                            </div>
                            <div className="bg-gray-50 rounded-2xl p-8">
                                <div className="text-orange-500 text-4xl font-bold mb-4">15K+</div>
                                <h4 className="font-bold text-lg mb-2">Leads Automated Daily</h4>
                                <p className="text-gray-600 text-sm mb-4">"Our team used to spend 4 hours a day on follow-ups. Now it's fully automated and we focus on closing."</p>
                                <p className="text-sm font-semibold">— Electromart UK</p>
                            </div>
                            <div className="bg-gray-50 rounded-2xl p-8">
                                <div className="text-orange-500 text-4xl font-bold mb-4">24/7</div>
                                <h4 className="font-bold text-lg mb-2">Never Miss A Lead</h4>
                                <p className="text-gray-600 text-sm mb-4">"Even at 2 AM, our AI is qualifying leads and booking test drives. It's like having a sales team that never sleeps."</p>
                                <p className="text-sm font-semibold">— AutoNation Birmingham</p>
                            </div>
                        </div>
                    </section>

                    {/* Process */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold mb-8 text-center">Your 90-Day Transformation</h2>
                        <div className="space-y-6">
                            <div className="flex items-start gap-6 bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl">
                                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                                <div>
                                    <h3 className="font-bold text-xl mb-2">Week 1-2: Setup & Integration</h3>
                                    <p className="text-gray-600">We connect your WhatsApp, email, CRM, and calendar. Train the AI on your brand voice and FAQs.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-6 bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl">
                                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                                <div>
                                    <h3 className="font-bold text-xl mb-2">Week 3-4: Testing & Refinement</h3>
                                    <p className="text-gray-600">We run live tests, adjust responses, and optimize your qualification flow based on real data.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-6 bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl">
                                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                                <div>
                                    <h3 className="font-bold text-xl mb-2">Week 5-12: Scale & Optimize</h3>
                                    <p className="text-gray-600">Your system is live. We monitor performance, add automations, and help you scale effortlessly.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Scarcity */}
                    <section className="mb-20 bg-red-50 border-2 border-red-300 rounded-3xl p-12">
                        <div className="flex items-start gap-6">
                            <Clock className="text-red-600 flex-shrink-0" size={48} />
                            <div>
                                <h2 className="text-3xl font-bold mb-4">We Only Take 5 New Clients Per Month</h2>
                                <p className="text-lg text-gray-700 mb-4">
                                    Why? Because proper automation setup requires deep integration, custom training, and ongoing optimization. We don't rush. We build systems that actually work.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Application CTA */}
                    <section className="text-center bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-3xl p-12">
                        <h2 className="text-4xl font-bold mb-6">Ready To Stop Losing Leads?</h2>
                        <p className="text-xl mb-8 opacity-90">
                            Answer a few quick questions to see if Qorvix is the right fit for your business.
                        </p>
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-white text-orange-600 px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-all inline-flex items-center gap-3"
                        >
                            Get Your Custom Plan <ArrowRight size={24} />
                        </button>
                        <p className="text-sm mt-4 opacity-75">Takes 3 minutes · No credit card required</p>
                    </section>
                </div>
            ) : (
                // Application Form
                <div className="max-w-3xl mx-auto px-4 py-20">
                    <div className="bg-white rounded-3xl shadow-2xl p-10">
                        {/* Progress Bar */}
                        <div className="mb-8">
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                                <span>Question {currentStep + 1} of 12</span>
                                <span>{Math.round(((currentStep + 1) / 12) * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${((currentStep + 1) / 12) * 100}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Question 1: Contact Information */}
                        {currentStep === 0 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">Let's start with your contact information</h2>
                                <p className="text-gray-600 mb-8">We'll send you a confirmation and next steps</p>
                                <div className="space-y-4">
                                    <input
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => handleChange('fullName', e.target.value)}
                                        placeholder="Full Name"
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                                        required
                                    />
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        placeholder="Email Address"
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                                        required
                                    />
                                    <div>
                                        <div className="flex gap-3">
                                            <CountryCodeSelect
                                                value={formData.countryIso2}
                                                onChange={handleCountryChange}
                                            />
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => handlePhoneChange(e.target.value)}
                                                placeholder="Phone Number"
                                                aria-invalid={phoneError}
                                                className={`w-full p-4 border-2 rounded-lg focus:outline-none ${
                                                    phoneError
                                                        ? 'border-red-500 focus:border-red-500'
                                                        : 'border-gray-200 focus:border-orange-500'
                                                }`}
                                                required
                                            />
                                        </div>
                                        {phoneError && (
                                            <p className="mt-2 text-sm text-red-600">
                                                Enter a valid phone number for {formData.countryCode} (no need to include the country code).
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (formData.fullName && formData.email && phoneValid) {
                                                savePartialLead();
                                                handleNext();
                                            } else if (!phoneValid) {
                                                alert('Please enter a valid phone number for the selected country.');
                                            } else {
                                                alert('Please fill in all fields');
                                            }
                                        }}
                                        disabled={!formData.fullName || !formData.email || !phoneValid}
                                        className={`w-full p-4 rounded-lg font-semibold transition-all ${
                                            !formData.fullName || !formData.email || !phoneValid
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                    >
                                        Next Step →
                                    </button>
                                </div>
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="mt-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                >
                                    ← Back to overview
                                </button>
                            </div>
                        )}

                        {/* Question 2: Business Information */}
                        {currentStep === 1 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">Tell us about your business</h2>
                                <p className="text-gray-600 mb-8">We'd love to learn more about your company</p>
                                <div className="space-y-4">
                                    <input
                                        type="text"
                                        value={formData.companyName}
                                        onChange={(e) => handleChange('companyName', e.target.value)}
                                        placeholder="Company Name"
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                                        required
                                    />
                                    {!noWebsite && (
                                        <input
                                            type="url"
                                            value={formData.website}
                                            onChange={(e) => handleChange('website', e.target.value)}
                                            placeholder="Company Website (e.g. https://example.com)"
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                                        />
                                    )}
                                    <label className="flex items-center gap-3 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={noWebsite}
                                            onChange={(e) => {
                                                setNoWebsite(e.target.checked);
                                                if (e.target.checked) handleChange('website', 'N/A');
                                                else handleChange('website', '');
                                            }}
                                            className="w-5 h-5 accent-orange-500 rounded"
                                        />
                                        <span className="text-gray-600">I don't have a website yet</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.companyAddress}
                                        onChange={(e) => handleChange('companyAddress', e.target.value)}
                                        placeholder="Company Address (City, Country)"
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                                    />
                                    <textarea
                                        value={formData.companyDescription}
                                        onChange={(e) => handleChange('companyDescription', e.target.value)}
                                        rows="3"
                                        placeholder="Brief description of what your company does..."
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none resize-none"
                                    ></textarea>
                                    <button
                                        onClick={() => {
                                            if (!formData.companyName) {
                                                alert('Please enter your company name');
                                            } else if (!noWebsite && !formData.website) {
                                                alert('Please enter your website or check "I don\'t have a website yet"');
                                            } else {
                                                handleNext();
                                            }
                                        }}
                                        className="w-full p-4 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all"
                                    >
                                        Next Step →
                                    </button>
                                </div>
                                <button
                                    onClick={() => setCurrentStep(0)}
                                    className="mt-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                >
                                    ← Back
                                </button>
                            </div>
                        )}

                        {/* Question 3: Business Type */}
                        {currentStep === 2 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">What best describes your business?</h2>
                                <p className="text-gray-600 mb-8">This helps us understand your industry context</p>
                                <div className="space-y-3">
                                    {['Service-based', 'E-commerce', 'Agency', 'SaaS', 'Local business', 'Other'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('businessType', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="mt-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                >
                                    ← Back to overview
                                </button>
                            </div>
                        )}

                        {/* Question 4: Growth Stage */}
                        {currentStep === 3 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">Which stage are you in right now?</h2>
                                <p className="text-gray-600 mb-8">Understanding your growth phase helps us tailor the right solution</p>
                                <div className="space-y-3">
                                    {['Early growth', 'Scaling', 'Established but plateauing', 'Launch phase'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('growthStage', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 5: Current Bottleneck */}
                        {currentStep === 4 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">Where is your biggest operational challenge?</h2>
                                <p className="text-gray-600 mb-8">This tells us where automation becomes most valuable</p>
                                <div className="space-y-3">
                                    {['Lead generation', 'Lead follow-up', 'Sales process', 'Client onboarding', 'Internal workflows', 'Content / marketing execution'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('bottleneck', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 6: Current System Reality */}
                        {currentStep === 5 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">How are you handling this today?</h2>
                                <p className="text-gray-600 mb-8">Time to confront the current inefficiency</p>
                                <div className="space-y-3">
                                    {['Manual processes', 'Multiple disconnected tools', 'Team-driven but slow', 'No defined system'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('currentSystem', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 7: Business Impact */}
                        {currentStep === 6 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">What happens if this problem continues?</h2>
                                <p className="text-gray-600 mb-8">Now urgency becomes real</p>
                                <div className="space-y-3">
                                    {['Lost leads', 'Slower growth', 'Team overload', 'Revenue leakage', 'Inconsistent customer experience'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('businessImpact', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 8: Desired Outcome */}
                        {currentStep === 7 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">In the next 90 days, what would success look like?</h2>
                                <p className="text-gray-600 mb-8">Paint the picture of your ideal future state</p>
                                <div className="space-y-3">
                                    {['Faster lead handling', 'More consistent sales', 'Reduced manual work', 'Better customer journey', 'Scalable systems'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('desiredOutcome', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 9: Investment Readiness */}
                        {currentStep === 8 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">What monthly budget do you allocate to growth systems?</h2>
                                <p className="text-gray-600 mb-8">This helps us recommend the right plan</p>
                                <div className="space-y-3">
                                    {['Under $1k', '$1k–$3k', '$3k–$10k', '$10k+'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('budget', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 10: Timeline */}
                        {currentStep === 9 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">When are you looking to improve your systems?</h2>
                                <p className="text-gray-600 mb-8">Timing is everything</p>
                                <div className="space-y-3">
                                    {['Immediately', 'Within 30 days', '60–90 days', 'Just exploring'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('timeline', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 11: Decision Role */}
                        {currentStep === 10 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">Are you the decision maker?</h2>
                                <p className="text-gray-600 mb-8">Understanding decision authority helps us serve you better</p>
                                <div className="space-y-3">
                                    {['Yes', 'Part of decision team', 'No'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => { handleChange('decisionRole', option); handleNext(); }}
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Question 12: Motivation */}
                        {currentStep === 11 && (
                            <div>
                                <h2 className="text-3xl font-bold mb-4">Why are you considering system automation right now?</h2>
                                <p className="text-gray-600 mb-8">Help us understand your trigger moment</p>
                                <textarea
                                    value={formData.motivation}
                                    onChange={(e) => handleChange('motivation', e.target.value)}
                                    rows="6"
                                    placeholder="Share what's driving this decision..."
                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none resize-none mb-6"
                                ></textarea>

                                {/* Success Message */}
                                {submitSuccess && (
                                    <div className="mb-6 bg-green-50 border-2 border-green-500 text-green-800 px-6 py-5 rounded-lg">
                                        <div className="flex items-start gap-3 mb-4">
                                            <Check size={24} className="flex-shrink-0" />
                                            <div>
                                                <p className="font-bold text-lg">Application Submitted Successfully!</p>
                                                <p className="text-sm">Check your email for confirmation.</p>
                                            </div>
                                        </div>
                                        <a
                                            href={WHATSAPP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-3 transition-all"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                                            </svg>
                                            Open WhatsApp Now
                                        </a>
                                        <p className="text-xs text-center mt-3">If WhatsApp doesn't open automatically, tap the button above.</p>
                                    </div>
                                )}

                                {!submitSuccess && (
                                    <button
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                        className="w-full bg-orange-500 text-white py-4 rounded-lg font-bold text-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-3 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                                        {!isSubmitting && <ArrowRight size={24} />}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Back Button */}
                        {currentStep > 0 && (
                            <button
                                onClick={() => setCurrentStep(currentStep - 1)}
                                className="mt-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                            >
                                ← Back
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">Documentation</Link>
                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">Integration</Link>
                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">FAQs</Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}